import MarkdownIt from 'markdown-it';
import { cleanReportPromptText } from './reportPromptText.js';

type Token = { type: string; tag: string; content: string; children?: Token[] | null; attrGet(name: string): string | null };
// The app's older ambient MarkdownIt declarations omit the parser API.
const markdown = new MarkdownIt({ html: false, linkify: true }) as unknown as { parse(source: string, environment: object): Token[] };
export type EpProjectTextRun = { text: string; bold?: boolean; italic?: boolean; link?: string };
export type EpProjectDocumentBlock =
    | { kind: 'heading'; level: number; runs: EpProjectTextRun[] }
    | { kind: 'paragraph'; runs: EpProjectTextRun[]; indent: number; marker?: string; quote?: boolean }
    | { kind: 'table'; headers: EpProjectTextRun[][]; rows: EpProjectTextRun[][][] }
    | { kind: 'rule' };

const label = (key: string) => key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
    .replace(/^./, character => character.toUpperCase());
const scalar = (value: unknown): value is string | number | boolean => value !== null && ['string', 'number', 'boolean'].includes(typeof value);
const cell = (value: unknown) => String(value ?? '').replace(/\|/g, '\\|').replace(/\r?\n/g, ' / ');
const table = (headers: string[], rows: unknown[][]) => [headers.map(cell).join(' | '), headers.map(() => '---').join(' | '),
    ...rows.map(row => row.map(cell).join(' | '))].map(row => `| ${row} |`).join('\n');

function formatData(value: unknown, depth = 0, level = 2): string {
    if (depth > 16 || value == null)
        return '';
    if (typeof value === 'string') {
        const source = value.trim();
        if (!source)
            return '';
        try {
            const parsed = JSON.parse(source);
            if (typeof parsed === 'object' || typeof parsed === 'string' && parsed !== source)
                return formatData(parsed, depth + 1, level);
        } catch { /* Ordinary report prose is already Markdown. */ }
        const fenced = source.replace(/```(?:json)?\s*\n?([\s\S]*?)```/gi, (match, body: string) => {
            try { return formatData(JSON.parse(body.trim()), depth + 1, level); }
            catch { return /^```json/i.test(match) ? 'Report data needs consultant review.' : match; }
        });
        if (/^\{\s*"[^"\n]+"\s*:/.test(fenced) || /^\[\s*(?:\{|")/.test(fenced)) {
            let quoted = false;
            let escaped = false;
            let nesting = 0;
            for (let index = 0; index < fenced.length; index++) {
                const character = fenced[index];
                if (escaped) { escaped = false; continue; }
                if (quoted && character === '\\') { escaped = true; continue; }
                if (character === '"')
                    quoted = !quoted;
                if (quoted)
                    continue;
                if (character === '{' || character === '[')
                    nesting++;
                else if (character === '}' || character === ']')
                    nesting--;
                if (!nesting) {
                    try { return [formatData(JSON.parse(fenced.slice(0, index + 1)), depth + 1, level),
                        formatData(fenced.slice(index + 1), depth + 1, level)].filter(Boolean).join('\n\n'); }
                    catch { break; }
                }
            }
            return 'Report data needs consultant review.';
        }
        return fenced;
    }
    if (Array.isArray(value)) {
        if (value.every(scalar))
            return value.map(item => `- ${String(item)}`).join('\n');
        if (value.length && value.every(item => item && typeof item === 'object' && !Array.isArray(item) &&
            Object.values(item).every(itemValue => scalar(itemValue) || itemValue == null) &&
            !('content' in item || 'body' in item || 'markdown' in item))) {
            const keys = [...new Set(value.flatMap(item => Object.keys(item)))];
            if (keys.length <= 6)
                return table(keys.map(label), value.map(item => keys.map(key => item[key])));
        }
        return value.map(item => formatData(item, depth + 1, level)).filter(Boolean).join('\n\n');
    }
    if (typeof value === 'object') {
        const entries = Object.entries(value).filter(([, item]) => item != null && item !== '');
        const bodyKeys = new Set(['content', 'markdown', 'body', 'text']);
        const title = entries.find(([key, item]) => ['title', 'heading', 'sectionTitle'].includes(key) && typeof item === 'string');
        const fields = entries.filter(([key, item]) => scalar(item) && !bodyKeys.has(key) && key !== title?.[0] &&
            (typeof item !== 'string' || !item.includes('\n') && !/^\s*[\[{]/.test(item)));
        const parts: string[] = [];
        if (title)
            parts.push(`${'#'.repeat(Math.min(level, 6))} ${title[1]}`);
        if (fields.length)
            parts.push(table(['Field', 'Detail'], fields.map(([key, item]) => [label(key), item])));
        for (const [key, item] of entries) {
            if (key === title?.[0] || fields.some(([field]) => field === key))
                continue;
            const unwrapped = bodyKeys.has(key) || key === 'sections';
            const body = formatData(item, depth + 1, unwrapped ? level + (title ? 1 : 0) : level + 1);
            if (body)
                parts.push(unwrapped ? body : `${'#'.repeat(Math.min(level, 6))} ${label(key)}\n\n${body}`);
        }
        return parts.join('\n\n');
    }
    return String(value);
}

export const epProjectDocumentMarkdown = (value: unknown): string => formatData(cleanReportPromptText(value));

export const epProjectStatusTone = (value: string): 'danger' | 'warning' | 'success' | 'neutral' => {
    const status = value.trim().toLowerCase();
    if (/^(critical|high|blocked)$/.test(status)) return 'danger';
    if (/^(medium|pending|open|unverified|in progress|under review)$/.test(status)) return 'warning';
    if (/^(low|verified|complete|completed|confirmed|approved)$/.test(status)) return 'success';
    return 'neutral';
};

function inlineRuns(tokens: Token[] = []): EpProjectTextRun[] {
    const runs: EpProjectTextRun[] = [];
    let bold = 0;
    let italic = 0;
    let link: string | undefined;
    for (const token of tokens) {
        if (token.type === 'strong_open') bold++;
        else if (token.type === 'strong_close') bold--;
        else if (token.type === 'em_open') italic++;
        else if (token.type === 'em_close') italic--;
        else if (token.type === 'link_open') link = token.attrGet('href') || undefined;
        else if (token.type === 'link_close') link = undefined;
        else if (['text', 'code_inline', 'image', 'softbreak', 'hardbreak'].includes(token.type) && (token.content || token.type.endsWith('break')))
            runs.push({ text: token.type.endsWith('break') ? '\n' : token.content, bold: Boolean(bold), italic: Boolean(italic), link });
    }
    return runs;
}

export function epProjectDocumentBlocks(value: unknown): EpProjectDocumentBlock[] {
    const tokens = markdown.parse(epProjectDocumentMarkdown(value), {});
    const blocks: EpProjectDocumentBlock[] = [];
    const lists: Array<{ ordered: boolean; next: number }> = [];
    let marker: string | undefined;
    let quote = 0;
    for (let index = 0; index < tokens.length; index++) {
        const token = tokens[index];
        if (token.type === 'table_open') {
            const headers: EpProjectTextRun[][] = [];
            const rows: EpProjectTextRun[][][] = [];
            let row: EpProjectTextRun[][] = [];
            let header = false;
            while (++index < tokens.length && tokens[index].type !== 'table_close') {
                const item = tokens[index];
                if (item.type === 'thead_open') header = true;
                else if (item.type === 'thead_close') header = false;
                else if (item.type === 'tr_open') row = [];
                else if (item.type === 'inline') row.push(inlineRuns(item.children || []));
                else if (item.type === 'tr_close') {
                    if (header) headers.push(...row);
                    else rows.push(row);
                }
            }
            blocks.push({ kind: 'table', headers, rows });
        } else if (token.type === 'heading_open')
            blocks.push({ kind: 'heading', level: Number(token.tag.slice(1)), runs: inlineRuns(tokens[++index]?.children || []) });
        else if (token.type === 'bullet_list_open' || token.type === 'ordered_list_open')
            lists.push({ ordered: token.type === 'ordered_list_open', next: Number(token.attrGet('start') || 1) });
        else if (token.type === 'bullet_list_close' || token.type === 'ordered_list_close')
            lists.pop();
        else if (token.type === 'list_item_open') {
            const list = lists[lists.length - 1];
            marker = list.ordered ? `${list.next++}.` : '-';
        } else if (token.type === 'list_item_close') marker = undefined;
        else if (token.type === 'blockquote_open') quote++;
        else if (token.type === 'blockquote_close') quote--;
        else if (token.type === 'inline') {
            blocks.push({ kind: 'paragraph', runs: inlineRuns(token.children || []), indent: lists.length, marker, quote: Boolean(quote) });
            marker = undefined;
        } else if (token.type === 'fence' || token.type === 'code_block')
            blocks.push({ kind: 'paragraph', runs: [{ text: token.content }], indent: 0 });
        else if (token.type === 'hr') blocks.push({ kind: 'rule' });
    }
    return blocks;
}
